import { energyType } from 'common'
import Button from 'components/Button'
import ButtonLink from 'components/ButtonLink'
import { PrefixedInput } from 'components/Input'
import Table from 'components/Table'
import copy from 'copy.json'
import { LockIcon, LockOpenIcon } from 'icons'
import { useState } from 'react'
import styled from 'styled-components'
import { MatchingOffers, MatchOptions, MatchTranche, PublicMatchingOffers } from 'types/match'
import { polishPlural, toCurrency } from 'utils'
import { aggrTranchePrice, sumTranchePower } from 'utils/tranche-utils'

const VISIBLE_ROWS = 3
const texts = copy.page.matches

type MatchOffersTableProps = {
  useMatchId: boolean
  data: MatchingOffers | PublicMatchingOffers
  offerOptions: MatchOptions & MatchTranche
  onChangeVolume?: (id: number, newValue: number) => void
  onChangePrice?: (id: number, newValue: number) => void
  onToggleLock?: (id: number) => void
  disabled?: boolean
}

export const MatchOffersTable = ({
  useMatchId,
  data,
  offerOptions,
  onChangeVolume,
  onChangePrice,
  onToggleLock,
  disabled
}: MatchOffersTableProps) => {
  const [isTableExpanded, setIsTableExpanded] = useState(false)

  const trancheMultiplier = ((data?.options?.tranche_energy_pricing_extra_percent || 0) + 100) / 100
  console.log('🚀 ~ trancheMultiplier:', trancheMultiplier)

  const baseYPowerSum = sumTranchePower('base', 'y', data?.tranche)
  const baseQPowerSum = sumTranchePower('base', 'q', data?.tranche)
  const baseMPowerSum = sumTranchePower('base', 'm', data?.tranche)
  const baseQMinPrice = aggrTranchePrice('base', 'q', Math.min, data?.tranche) * trancheMultiplier
  const baseMMinPrice = aggrTranchePrice('base', 'm', Math.min, data?.tranche) * trancheMultiplier
  const baseQMaxPrice = aggrTranchePrice('base', 'q', Math.max, data?.tranche) * trancheMultiplier
  const baseMMaxPrice = aggrTranchePrice('base', 'm', Math.max, data?.tranche) * trancheMultiplier
  const peakYPowerSum = sumTranchePower('peak', 'y', data?.tranche)
  const peakQPowerSum = sumTranchePower('peak', 'q', data?.tranche)
  const peakMPowerSum = sumTranchePower('peak', 'm', data?.tranche)
  const peakQMinPrice = aggrTranchePrice('peak', 'q', Math.min, data?.tranche) * trancheMultiplier
  const peakMMinPrice = aggrTranchePrice('peak', 'm', Math.min, data?.tranche) * trancheMultiplier
  const peakQMaxPrice = aggrTranchePrice('peak', 'q', Math.max, data?.tranche) * trancheMultiplier
  const peakMMaxPrice = aggrTranchePrice('peak', 'm', Math.max, data?.tranche) * trancheMultiplier

  return (
    <OffersTable>
      {!onChangeVolume && <h3>{texts.heading_offers_table}</h3>}

      <HorizontalScroll>
        <Table>
          <thead>
            <tr>
              <th style={{ minWidth: 160 }}>{texts.offers_table.energy_type}</th>
              {useMatchId && <th style={{ minWidth: 80 }}>{texts.offers_table.offer_id}</th>}
              <th style={{ minWidth: 160 }}>{texts.offers_table.total_power}</th>
              <th style={{ minWidth: 150 }}>
                {onChangePrice !== undefined ? 'Cena' : texts.offers_table.energy_price}
              </th>
            </tr>
          </thead>

          <tbody>
            {data.matched_offers_data
              .filter((_, index) => isTableExpanded || index < VISIBLE_ROWS)
              .map((offer, index) => {
                const customOfferData = offerOptions.custom_offers_data.find(
                  ({ offer_id }) => offer_id === offer.offer_id
                )

                return (
                  <tr key={offer.offer_id || index}>
                    <td>{energyType[offer.energy_type]}</td>
                    {useMatchId && <td>{offer.offer_id}</td>}
                    <td>
                      {onChangeVolume !== undefined && onToggleLock !== undefined ? (
                        <>
                          <PrefixedInput
                            name={`custom_offer_volume_${offer.offer_id}`}
                            type="number"
                            value={customOfferData?.total_power ?? offer.total_power}
                            prefix={copy.common.MWh}
                            prefixWidth="50px"
                            reverse
                            min={0}
                            style={{ minWidth: 150, height: 30, float: 'left' }}
                            onChange={e => onChangeVolume(offer.offer_id, Number(e.target.value))}
                            disabled={disabled}
                          />
                          <Button
                            secondary
                            small
                            style={{ padding: 6 }}
                            onClick={() => onToggleLock(offer.offer_id)}
                            disabled={disabled}
                            title="Zablokuj wolumen do automatycznego dopasowania kontraktów"
                          >
                            {customOfferData?.lock !== false ? (
                              <LockIcon width={16} height={16} />
                            ) : (
                              <LockOpenIcon width={16} height={16} />
                            )}
                          </Button>
                        </>
                      ) : (
                        <>
                          {offer.total_power} {copy.common.MWh}
                        </>
                      )}
                    </td>
                    <td>
                      {onChangePrice !== undefined ? (
                        <PrefixedInput
                          name={`custom_offer_price_${offer.offer_id}`}
                          type="number"
                          value={customOfferData?.energy_price ?? offer.energy_price}
                          prefix={copy.common.zl_per_mwh}
                          prefixWidth="70px"
                          reverse
                          min={0}
                          style={{ width: 150, height: 30 }}
                          onChange={e => onChangePrice(offer.offer_id, Number(e.target.value))}
                          disabled={disabled}
                        />
                      ) : (
                        toCurrency(offer.energy_price)
                      )}
                    </td>
                  </tr>
                )
              })}
            {!onChangeVolume && offerOptions.base_y_power ? (
              <tr>
                <td>{copy.page.matches.offers_table.base_y}</td>
                <td></td>
                <td>
                  {baseYPowerSum} {copy.common.MWh}
                </td>
                <td>{toCurrency((offerOptions.base_y_price ?? 0) * trancheMultiplier)}</td>
              </tr>
            ) : null}
            {!onChangeVolume && baseQPowerSum > 0 && (
              <tr>
                <td>{copy.page.matches.offers_table.base_q}</td>
                <td></td>
                <td>
                  {baseQPowerSum} {copy.common.MWh}
                </td>
                <td>
                  {toCurrency(Number.isFinite(baseQMinPrice) ? baseQMinPrice : 0)} -{' '}
                  {toCurrency(Number.isFinite(baseQMaxPrice) ? baseQMaxPrice : 0)}
                </td>
              </tr>
            )}
            {!onChangeVolume && baseMPowerSum > 0 && (
              <tr>
                <td>{copy.page.matches.offers_table.base_m}</td>
                <td></td>
                <td>
                  {baseMPowerSum} {copy.common.MWh}
                </td>
                <td>
                  {toCurrency(Number.isFinite(baseMMinPrice) ? baseMMinPrice : 0)} -{' '}
                  {toCurrency(Number.isFinite(baseMMaxPrice) ? baseMMaxPrice : 0)}
                </td>
              </tr>
            )}
            {!onChangeVolume && offerOptions.peak_y_power ? (
              <tr>
                <td>{copy.page.matches.offers_table.peak_y}</td>
                <td></td>
                <td>
                  {peakYPowerSum} {copy.common.MWh}
                </td>
                <td>{toCurrency((offerOptions.peak_y_price ?? 0) * trancheMultiplier)}</td>
              </tr>
            ) : null}
            {!onChangeVolume && peakQPowerSum > 0 && (
              <tr>
                <td>{copy.page.matches.offers_table.peak_q}</td>
                <td></td>
                <td>
                  {peakQPowerSum} {copy.common.MWh}
                </td>
                <td>
                  {toCurrency(Number.isFinite(peakQMinPrice) ? peakQMinPrice : 0)} -{' '}
                  {toCurrency(Number.isFinite(peakQMaxPrice) ? peakQMaxPrice : 0)}
                </td>
              </tr>
            )}
            {!onChangeVolume && peakMPowerSum > 0 && (
              <tr>
                <td>{copy.page.matches.offers_table.peak_m}</td>
                <td></td>
                <td>
                  {peakMPowerSum} {copy.common.MWh}
                </td>
                <td>
                  {toCurrency(Number.isFinite(peakMMinPrice) ? peakMMinPrice : 0)} -{' '}
                  {toCurrency(Number.isFinite(peakMMaxPrice) ? peakMMaxPrice : 0)}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </HorizontalScroll>

      {data.matched_offers_data.length > VISIBLE_ROWS && (
        <ShowMoreOffers>
          {!isTableExpanded && (
            <span>
              +{data.matched_offers_data.length - VISIBLE_ROWS}{' '}
              {polishPlural(
                data.matched_offers_data.length - VISIBLE_ROWS,
                texts.offers_table.other_offers.singular_nominative,
                texts.offers_table.other_offers.plural_nominative,
                texts.offers_table.other_offers.plural_genitive
              )}
            </span>
          )}

          <ButtonLink onClick={() => setIsTableExpanded(state => !state)}>
            {isTableExpanded ? texts.offers_table.hide_rest : texts.offers_table.show_rest}
          </ButtonLink>
        </ShowMoreOffers>
      )}
    </OffersTable>
  )
}

const OffersTable = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const ShowMoreOffers = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const HorizontalScroll = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
`
